// ./src/components/features/VolunteerCheckin/VolunteerCheckinFilterBar/VolunteerCheckinFilterBar.tsx
//
// Filter controls for the volunteer Check-in roster: by attendance status and by role.
// Fully controlled — the page owns the value and applies it client-side to the roster.

import React from "react";
import { Group, Box, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CheckableMultiSelect } from "@/components/generic";

import { CheckinStatus } from "../VolunteerCheckinList/VolunteerCheckinList";

export interface VolunteerCheckinFilterValue {
  statuses: CheckinStatus[];
  roleIds: string[];
}

export interface CheckinRoleOption {
  value: string;
  label: string;
}

export const EMPTY_VOLUNTEER_CHECKIN_FILTER: VolunteerCheckinFilterValue = {
  statuses: [],
  roleIds: [],
};

export interface VolunteerCheckinFilterBarProps {
  value: VolunteerCheckinFilterValue;
  onChange: (value: VolunteerCheckinFilterValue) => void;
  roleOptions: CheckinRoleOption[];
  disabled?: boolean;
  showClear?: boolean;
}

const isFilterActive = (value: VolunteerCheckinFilterValue): boolean => value.statuses.length > 0 || value.roleIds.length > 0;

const FACET_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const VolunteerCheckinFilterBarComponent: React.FC<VolunteerCheckinFilterBarProps> = ({
  value,
  onChange,
  roleOptions,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["volunteers", "common"]);

  const statusOptions: { value: CheckinStatus; label: string }[] = [
    { value: "expected", label: t("volunteers:checkin.status.expected", { defaultValue: "Expected" }) },
    { value: "onsite", label: t("volunteers:checkin.status.onsite", { defaultValue: "On-site" }) },
    { value: "left", label: t("volunteers:checkin.status.left", { defaultValue: "Left" }) },
    { value: "noshow", label: t("volunteers:checkin.status.noshow", { defaultValue: "No-show" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={FACET_STYLE}>
        <CheckableMultiSelect
          label={t("volunteers:checkin.filters.status", { defaultValue: "Status" })}
          placeholder={t("volunteers:checkin.filters.statusPh", { defaultValue: "Any status" })}
          data={statusOptions}
          value={value.statuses}
          onChange={(ids) => onChange({ ...value, statuses: ids as CheckinStatus[] })}
          disabled={disabled}
          size="md"
        />
      </Box>

      <Box style={FACET_STYLE}>
        <CheckableMultiSelect
          label={t("volunteers:checkin.filters.role", { defaultValue: "Role" })}
          placeholder={t("volunteers:checkin.filters.rolePh", { defaultValue: "All roles" })}
          data={roleOptions}
          value={value.roleIds}
          onChange={(ids) => onChange({ ...value, roleIds: ids })}
          disabled={disabled || roleOptions.length === 0}
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_VOLUNTEER_CHECKIN_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const VolunteerCheckinFilterBar = React.memo(VolunteerCheckinFilterBarComponent);
